import { z } from "zod";
import {
  EvalBatchStatusSchema,
  EvalComparisonStatusSchema,
  EvalWinnerSchema,
} from "../../enums/evals";

/**
 * Admin batch surface: `POST /api/evals/batches`, `GET /api/evals/batches`,
 * `GET /api/evals/batches/:batchId`.
 *
 * Creating a batch is a 202: it inserts the whole comparison fan-out and
 * constructs each control run, then the backend's operational tick drives every
 * comparison the rest of the way on its own.
 */

export const EvalVariantInputSchema = z
  .object({
    label: z.string().min(1),
    // Becomes the run's yeet config override, so every analysis publish of that
    // run merges it over the AppConfig-resolved config.
    agentModel: z.string().min(1),
    // Omitted = the yeet-agent surface's active bundle, which is the control.
    promptBundleId: z.uuid().nullish(),
  })
  .strict();
export type EvalVariantInput = z.infer<typeof EvalVariantInputSchema>;

export const EvalBatchCreateRequestSchema = z
  .object({
    suiteId: z.uuid(),
    variantA: EvalVariantInputSchema,
    variantB: EvalVariantInputSchema,
  })
  .strict();
export type EvalBatchCreateRequest = z.infer<
  typeof EvalBatchCreateRequestSchema
>;

export const EvalBatchCreateResponseSchema = z.object({ batchId: z.uuid() });
export type EvalBatchCreateResponse = z.infer<
  typeof EvalBatchCreateResponseSchema
>;

/** Comparison counts per status; every status is present, zero included. */
export const EvalComparisonCountsSchema = z.record(
  EvalComparisonStatusSchema,
  z.number().int().min(0),
);
export type EvalComparisonCounts = z.infer<typeof EvalComparisonCountsSchema>;

export const EvalVariantSummarySchema = z.object({
  label: z.string(),
  agentModel: z.string(),
  promptBundleId: z.uuid().nullable(),
});
export type EvalVariantSummary = z.infer<typeof EvalVariantSummarySchema>;

export const EvalBatchSummarySchema = z.object({
  batchId: z.uuid(),
  suiteId: z.uuid(),
  suiteName: z.string(),
  status: EvalBatchStatusSchema,
  createdAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  variantA: EvalVariantSummarySchema,
  variantB: EvalVariantSummarySchema,
  comparisonCounts: EvalComparisonCountsSchema,
});
export type EvalBatchSummary = z.infer<typeof EvalBatchSummarySchema>;

export const EvalBatchListResponseSchema = z.array(EvalBatchSummarySchema);
export type EvalBatchListResponse = z.infer<typeof EvalBatchListResponseSchema>;

export const EvalComparisonSummarySchema = z.object({
  comparisonId: z.uuid(),
  websiteId: z.uuid(),
  originUrl: z.string(),
  runAId: z.uuid().nullable(),
  runBId: z.uuid().nullable(),
  status: EvalComparisonStatusSchema,
  // The standing verdict: a human judgment where one exists, otherwise the
  // LLM's, otherwise null because nothing has been judged yet.
  winner: EvalWinnerSchema.nullable(),
  // Whether the pair analysed identical evidence, by the same digest-set test
  // the detail view uses. Null until both runs have a capture; false means the
  // verdict beside it says more about the fetch than about the agents, which
  // the batch table has to show or a whole batch can read as clean while every
  // row in it compared different bytes.
  evidenceMatches: z.boolean().nullable(),
  error: z.string().nullable(),
});
export type EvalComparisonSummary = z.infer<typeof EvalComparisonSummarySchema>;

export const EvalBatchDetailResponseSchema = EvalBatchSummarySchema.extend({
  comparisons: z.array(EvalComparisonSummarySchema),
});
export type EvalBatchDetailResponse = z.infer<
  typeof EvalBatchDetailResponseSchema
>;
